import { CartAddEvent } from '@theme/events';

class OmerellaVideoReels extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {HTMLVideoElement | null} */
  #video = null;

  /** @type {HTMLVideoElement | null} */
  #prevVideo = null;

  /** @type {HTMLVideoElement | null} */
  #nextVideo = null;

  /** @type {HTMLElement | null} */
  #ring = null;

  /** @type {HTMLElement | null} */
  #progressBar = null;

  /** @type {number} */
  #currentIndex = 0;

  /** @type {HTMLElement[]} */
  #cards = [];

  /** @type {HTMLVideoElement[]} */
  #cardVideos = [];

  /** @type {object[]} */
  #reels = [];

  /** @type {IntersectionObserver | null} */
  #cardObserver = null;

  /** @type {(() => void) | null} */
  #onTimeUpdate = null;

  connectedCallback() {
    this.#dialog = this.querySelector('.omerella-reel-modal');
    this.#video = this.querySelector('.omerella-reel-modal__video');
    this.#prevVideo = this.querySelector('.omerella-reel-modal__side--prev .omerella-reel-modal__side-video');
    this.#nextVideo = this.querySelector('.omerella-reel-modal__side--next .omerella-reel-modal__side-video');
    this.#ring = this.querySelector('.omerella-reel-modal__ring');
    this.#progressBar = this.querySelector('.omerella-reel-modal__progress-bar');
    this.#cards = [...this.querySelectorAll('.omerella-reel-card')];
    this.#cardVideos = [...this.querySelectorAll('.omerella-reel-card__video')];

    const dataEl = this.querySelector('[data-reels-json]');
    if (dataEl?.textContent) {
      try {
        this.#reels = JSON.parse(dataEl.textContent);
      } catch {
        this.#reels = [];
      }
    }

    this.#cards.forEach((card, index) => {
      card.addEventListener('click', () => this.#open(index));
    });

    this.querySelector('.omerella-reel-modal__close')?.addEventListener('click', () => this.#close());
    this.querySelector('.omerella-reel-modal__backdrop')?.addEventListener('click', () => this.#close());
    this.querySelector('.omerella-reel-modal__nav--prev')?.addEventListener('click', () => this.#navigate(-1));
    this.querySelector('.omerella-reel-modal__nav--next')?.addEventListener('click', () => this.#navigate(1));
    this.querySelector('.omerella-reel-modal__side--prev')?.addEventListener('click', () => this.#navigate(-1));
    this.querySelector('.omerella-reel-modal__side--next')?.addEventListener('click', () => this.#navigate(1));
    this.querySelector('.omerella-reel-modal__mute')?.addEventListener('click', () => this.#toggleMute());
    this.querySelector('.omerella-reel-modal__cart')?.addEventListener('click', (e) => this.#addToCart(e));
    this.querySelector('.omerella-reel-modal__whatsapp')?.addEventListener('click', () => this.#openWhatsApp());
    this.querySelector('.omerella-reel-modal__share-btn')?.addEventListener('click', () => this.#toggleShare());
    this.querySelector('.omerella-reel-modal__share-whatsapp')?.addEventListener('click', () => this.#shareWhatsApp());
    this.querySelector('.omerella-reel-modal__share-copy')?.addEventListener('click', () => this.#copyLink());
    this.querySelector('.omerella-reel-modal__share-more')?.addEventListener('click', () => this.#shareNative());
    this.querySelector('.omerella-reel-modal__share-panel')?.addEventListener('click', (e) => {
      if (e.target && e.target === e.currentTarget) this.#hideShare();
    });

    this.querySelector('.omerella-reel-modal__like')?.addEventListener('click', (e) => {
      const btn = /** @type {HTMLElement} */ (e.currentTarget);
      btn.classList.toggle('is-liked');
      const countEl = btn.querySelector('[data-like-count]');
      if (!countEl) return;
      const base = parseInt(btn.dataset.baseLikes || '0', 10);
      const liked = btn.classList.contains('is-liked');
      countEl.textContent = String(base + (liked ? 1 : 0));
    });

    this.#onTimeUpdate = () => this.#updateProgress();
    this.#video?.addEventListener('timeupdate', this.#onTimeUpdate);

    this.#dialog?.addEventListener('close', () => {
      this.#stopModalVideos();
      this.#resumeCardVideos();
    });

    document.addEventListener('keydown', this.#onKeydown);
    this.#setupCardAutoplay();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
    this.#cardObserver?.disconnect();
    if (this.#video && this.#onTimeUpdate) {
      this.#video.removeEventListener('timeupdate', this.#onTimeUpdate);
    }
  }

  #setupCardAutoplay() {
    if (!this.#cardVideos.length) return;

    this.#cardObserver = new IntersectionObserver(
      (entries) => {
        if (this.#dialog?.open) return;

        entries.forEach((entry) => {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) return;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
            video.muted = true;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: [0, 0.4, 0.75] }
    );

    this.#cardVideos.forEach((video) => {
      video.muted = true;
      video.defaultMuted = true;
      this.#cardObserver?.observe(video);
    });
  }

  #pauseCardVideos() {
    this.#cardVideos.forEach((video) => video.pause());
  }

  #resumeCardVideos() {
    if (this.#dialog?.open) return;

    this.#cardVideos.forEach((video) => {
      const rect = video.getBoundingClientRect();
      const inView = rect.left < window.innerWidth && rect.right > 0 && rect.top < window.innerHeight && rect.bottom > 0;
      if (inView) {
        video.muted = true;
        video.play().catch(() => {});
      }
    });
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!this.#dialog?.open) return;
    if (event.key === 'Escape') this.#close();
    if (event.key === 'ArrowLeft') this.#navigate(-1);
    if (event.key === 'ArrowRight') this.#navigate(1);
  };

  /** @param {number} index */
  #open(index) {
    if (!this.#dialog || !this.#reels[index]) return;
    this.#currentIndex = index;
    this.#pauseCardVideos();
    this.#dialog.showModal();
    document.documentElement.classList.add('omerella-reel-modal-open');
    this.#loadRing(index);
  }

  #close() {
    this.#dialog?.close();
    document.documentElement.classList.remove('omerella-reel-modal-open');
    this.#hideShare();
  }

  /** @param {number} direction */
  #navigate(direction) {
    if (!this.#reels.length) return;
    this.#ring?.classList.add('is-transitioning');
    this.#currentIndex = (this.#currentIndex + direction + this.#reels.length) % this.#reels.length;
    this.#loadRing(this.#currentIndex);
    window.setTimeout(() => this.#ring?.classList.remove('is-transitioning'), 350);
  }

  /**
   * @param {object} reel
   * @returns {string}
   */
  #resolveVideoUrl(reel) {
    if (!reel) return '';
    if (reel.mp4) return reel.mp4;

    const sources = reel.sources || [];
    const mp4 = sources.find(
      (/** @type {{url?: string, type?: string}} */ s) =>
        s.url && (s.type?.includes('mp4') || s.url.toLowerCase().includes('.mp4'))
    );
    if (mp4?.url) return mp4.url;

    return sources.find((/** @type {{url?: string}} */ s) => s.url)?.url || '';
  }

  /**
   * @param {HTMLVideoElement | null} videoEl
   * @param {number} index
   */
  #setVideoSource(videoEl, index) {
    if (!videoEl) return;
    const reel = this.#reels[index];
    if (!reel) return;

    const videoUrl = this.#resolveVideoUrl(reel);

    videoEl.pause();
    videoEl.removeAttribute('src');
    videoEl.innerHTML = '';

    if (videoUrl) {
      videoEl.src = videoUrl;
    } else if (reel.sources?.length) {
      reel.sources.forEach((/** @type {{url: string, type: string}} */ source) => {
        if (!source.url) return;
        const sourceEl = document.createElement('source');
        sourceEl.src = source.url;
        sourceEl.type = source.type || 'video/mp4';
        videoEl.appendChild(sourceEl);
      });
    }

    if (reel.poster) {
      videoEl.poster = reel.poster;
    } else {
      videoEl.removeAttribute('poster');
    }

    videoEl.muted = true;
    videoEl.defaultMuted = true;
    videoEl.loop = true;
    videoEl.playsInline = true;
    videoEl.setAttribute('playsinline', '');
    videoEl.setAttribute('webkit-playsinline', '');
    videoEl.preload = 'auto';
    videoEl.load();
  }

  /**
   * @param {HTMLVideoElement | null} videoEl
   */
  #playWhenReady(videoEl) {
    if (!videoEl) return;

    const attemptPlay = () => {
      if (!videoEl) return;
      videoEl.muted = true;
      videoEl.play().catch(() => {
        window.setTimeout(() => videoEl.play().catch(() => {}), 150);
      });
    };

    if (videoEl.readyState >= 2) {
      attemptPlay();
      return;
    }

    const onReady = () => attemptPlay();
    videoEl.addEventListener('loadeddata', onReady, { once: true });
    videoEl.addEventListener('canplay', onReady, { once: true });
  }

  /** @param {number} index */
  #loadRing(index) {
    const len = this.#reels.length;
    const prevIndex = (index - 1 + len) % len;
    const nextIndex = (index + 1) % len;

    if (this.#ring) {
      this.#ring.classList.toggle('omerella-reel-modal__ring--single', len <= 1);
    }

    if (len > 1) {
      this.#setVideoSource(this.#prevVideo, prevIndex);
      this.#setVideoSource(this.#nextVideo, nextIndex);
      this.#playWhenReady(this.#prevVideo);
      this.#playWhenReady(this.#nextVideo);
    } else {
      this.#prevVideo?.pause();
      this.#nextVideo?.pause();
    }

    this.#loadReel(index);
  }

  /** @param {number} index */
  #loadReel(index) {
    const reel = this.#reels[index];
    if (!reel || !this.#video) return;

    this.#setVideoSource(this.#video, index);
    this.#video.autoplay = true;
    this.#resetProgress();
    this.#playWhenReady(this.#video);

    const productImg = this.querySelector('.omerella-reel-modal__product-img');
    const productTitle = this.querySelector('.omerella-reel-modal__product-title');
    const productPrice = this.querySelector('.omerella-reel-modal__product-price');
    const cartBtn = this.querySelector('.omerella-reel-modal__cart');

    if (productImg instanceof HTMLImageElement) {
      productImg.src = reel.productImage || '';
      productImg.alt = reel.productTitle || '';
      productImg.hidden = !reel.productImage;
    }
    if (productTitle) productTitle.textContent = reel.productTitle || '';
    if (productPrice) productPrice.innerHTML = reel.productPrice || '';

    if (cartBtn instanceof HTMLButtonElement) {
      cartBtn.dataset.variantId = reel.variantId || '';
      cartBtn.disabled = !reel.canAddToCart;
      cartBtn.textContent = reel.canAddToCart
        ? this.dataset.addToCartLabel || 'ADD TO CART'
        : this.dataset.soldOutLabel || 'SOLD OUT';
    }

    const likeBtn = this.querySelector('.omerella-reel-modal__like');
    if (likeBtn instanceof HTMLElement) {
      likeBtn.classList.remove('is-liked');
      likeBtn.dataset.baseLikes = String(reel.likes || 0);
      const countEl = likeBtn.querySelector('[data-like-count]');
      if (countEl) countEl.textContent = String(reel.likes || 0);
    }

    const shareBtn = this.querySelector('.omerella-reel-modal__share-btn');
    if (shareBtn instanceof HTMLElement) {
      shareBtn.dataset.baseShares = String(reel.shares || 0);
      const shareCountEl = shareBtn.querySelector('[data-share-count]');
      if (shareCountEl) shareCountEl.textContent = String(reel.shares || 0);
    }

    this.#updateMuteIcon(true);
    this.#hideShare();
  }

  #resetProgress() {
    if (this.#progressBar) {
      this.#progressBar.style.width = '0%';
    }
  }

  #updateProgress() {
    if (!this.#video || !this.#progressBar) return;
    const { currentTime, duration } = this.#video;
    if (!duration || Number.isNaN(duration)) return;
    this.#progressBar.style.width = `${(currentTime / duration) * 100}%`;
  }

  #stopModalVideos() {
    [this.#video, this.#prevVideo, this.#nextVideo].forEach((video) => {
      if (!video) return;
      video.pause();
      video.removeAttribute('src');
      video.innerHTML = '';
      video.load();
    });
    this.#resetProgress();
  }

  #toggleMute() {
    if (!this.#video) return;
    const muted = !this.#video.muted;
    this.#video.muted = muted;
    this.#prevVideo && (this.#prevVideo.muted = true);
    this.#nextVideo && (this.#nextVideo.muted = true);
    this.#updateMuteIcon(muted);
  }

  /** @param {boolean} muted */
  #updateMuteIcon(muted) {
    const muteIcon = this.querySelector('.omerella-reel-modal__mute-icon--off');
    const unmuteIcon = this.querySelector('.omerella-reel-modal__mute-icon--on');
    muteIcon?.classList.toggle('hidden', !muted);
    unmuteIcon?.classList.toggle('hidden', muted);
  }

  /** @param {Event} event */
  async #addToCart(event) {
    event.preventDefault();
    const btn = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.disabled) return;

    btn.disabled = true;
    const originalText = btn.textContent;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Could not add to cart');

      document.dispatchEvent(
        new CartAddEvent(data, this.id, {
          source: 'omerella-video-reels',
          variantId: String(variantId),
          productId: this.#reels[this.#currentIndex]?.productId,
        })
      );

      btn.textContent = this.dataset.addedLabel || 'ADDED';
      window.setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1800);
    } catch {
      btn.textContent = this.dataset.errorLabel || 'ERROR';
      window.setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1800);
    }
  }

  #openWhatsApp() {
    const url = this.dataset.whatsappUrl;
    if (url) window.open(url, '_blank', 'noopener');
  }

  #toggleShare() {
    const panel = this.querySelector('.omerella-reel-modal__share-panel');
    panel?.classList.toggle('is-open');
  }

  #hideShare() {
    this.querySelector('.omerella-reel-modal__share-panel')?.classList.remove('is-open');
  }

  #shareWhatsApp() {
    const reel = this.#reels[this.#currentIndex];
    if (!reel?.productUrl) return;
    const text = encodeURIComponent(`${reel.productTitle} ${reel.productUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener');
    this.#hideShare();
  }

  async #copyLink() {
    const reel = this.#reels[this.#currentIndex];
    if (!reel?.productUrl) return;
    try {
      await navigator.clipboard.writeText(reel.productUrl);
    } catch {
      /* fallback ignored */
    }
    this.#hideShare();
  }

  #shareNative() {
    const reel = this.#reels[this.#currentIndex];
    if (!reel?.productUrl || !navigator.share) return;
    navigator.share({ title: reel.productTitle, url: reel.productUrl }).catch(() => {});
    this.#hideShare();
  }
}

if (!customElements.get('omerella-video-reels')) {
  customElements.define('omerella-video-reels', OmerellaVideoReels);
}
